'use client'

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
    Tawk_API?: {
      visitor?: { name?: string; email?: string; phone?: string }
      onLoad?: () => void
      setAttributes?: (attrs: Record<string, string>, cb: (error?: unknown) => void) => void
    }
  }
}

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { User } from '@supabase/supabase-js'
import { authService, clearAuthSession } from '@/lib/services/auth'
import { Tables } from '@/types/database'
import { sessionCache } from '@/lib/auth/NewSessionCache'
import { fetchTokenUser, getAccessToken, markSignedOut, onAuthTransition } from '@/lib/auth/accessToken'
import { setCurrentUser } from '@/lib/auth/currentUser'
import { reloadPage } from '@/lib/auth/hardNavigate'

import { logger } from '@/lib/utils/logger'
import { invalidateOnLogout, invalidateProfile } from '@/lib/cache/CacheInvalidation'
import { useQueryClient } from '@tanstack/react-query'
import { analytics } from '@/lib/analytics'

type Profile = Tables<'user_profiles'>

// Key to track which users have had their migrated profiles checked
const MIGRATED_PROFILE_LINKED_KEY = 'migrated_profile_linked'

/**
 * Check if we need to link a migrated profile for this user
 */
async function checkAndLinkMigratedProfile(userId: string): Promise<void> {
  try {
    const linkedUsers = JSON.parse(localStorage.getItem(MIGRATED_PROFILE_LINKED_KEY) || '{}')
    if (linkedUsers[userId]) {
      logger.dev('[Auth] User migrated profile already checked:', userId)
      return
    }

    logger.dev('[Auth] Checking for migrated profile...')

    const response = await fetch('/api/auth/link-migrated-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })

    const result = await response.json()

    if (response.ok) {
      linkedUsers[userId] = {
        checked: true,
        linked: result.linked,
        timestamp: new Date().toISOString()
      }
      localStorage.setItem(MIGRATED_PROFILE_LINKED_KEY, JSON.stringify(linkedUsers))

      if (result.linked && result.tablesUpdated) {
        logger.dev('[Auth] Successfully linked migrated profile!', result)
      }
    } else {
      logger.error('[Auth] Error checking migrated profile:', result.error)
    }
  } catch (error) {
    logger.error('[Auth] Network error checking migrated profile:', error)
  }
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  profileLoading: boolean
  signOut: () => Promise<void>
  signOutAllDevices: () => Promise<void>
  refreshProfile: (userId?: string, force?: boolean) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
  /**
   * Server-verified auth snapshot from the (main) layout: the user was
   * validated by middleware's getUser() on this very request, the profile
   * fetched server-side. Seeding state from it makes the first client render
   * match the SSR HTML – no Login-button flash, no hydration mismatch, and no
   * zombie sessions (the server, not a local cache, is the source of truth).
   */
  initialUser?: User | null
  initialProfile?: Profile | null
  /**
   * The server could not verify the session for this render (transient auth
   * failure). Not the same as signed out: ask the token route once on mount.
   */
  authUnknown?: boolean
}

export function AuthProvider({ children, initialUser = null, initialProfile = null, authUnknown = false }: AuthProviderProps) {
  const queryClient = useQueryClient()

  const [user, setUser] = useState<User | null>(() => {
    // Publish the server-verified user before any child effect can ask for it
    // (browser only – on the server a module global would outlive the request).
    if (typeof window !== 'undefined') setCurrentUser(initialUser)
    return initialUser
  })
  const [profile, setProfile] = useState<Profile | null>(initialProfile)
  // Auth state is known at render time (server snapshot), so there is no
  // loading phase. Kept in the context API for consumers that gate on it.
  const loading = false

  const [profileLoading, setProfileLoading] = useState(false)
  const userIdRef = useRef<string | undefined>(undefined)
  const tawkIdentifiedUserId = useRef<string | null>(null)

  // Keep the ref and the shared current-user store in step; announce identity
  // changes (QueryProvider refetches on them, never on token refreshes).
  const announcedUserId = useRef<string | null>(initialUser?.id ?? null)
  useEffect(() => {
    userIdRef.current = user?.id
    setCurrentUser(user)
    const id = user?.id ?? null
    if (id !== announcedUserId.current) {
      announcedUserId.current = id
      window.dispatchEvent(new CustomEvent('confy:auth-identity', { detail: { userId: id } }))
    }
  }, [user])

  const refreshProfile = useCallback(async (targetUserId?: string, force: boolean = false) => {
    const finalUserId = targetUserId || userIdRef.current

    if (!finalUserId) {
      setProfile(null)
      return
    }

    if (force) {
      invalidateProfile(finalUserId)
    }

    // Check cache first (profile cache is still useful – avoids redundant fetches)
    if (!force) {
      const cachedProfile = sessionCache.getProfile(finalUserId)
      if (cachedProfile) {
        setProfile(cachedProfile)
        setProfileLoading(false)
        return
      }
    }

    setProfileLoading(true)

    try {
      const profileData = await authService.getProfile(finalUserId)

      if (profileData) {
        setProfile(profileData)
        sessionCache.setProfile(profileData, finalUserId)
      }
    } catch (error) {
      logger.error('[Auth] Failed to fetch profile:', error)
    } finally {
      setProfileLoading(false)
    }
  }, [])

  // PostHog user identification
  useEffect(() => {
    if (user && profile) {
      const name = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      analytics.identify(user.id, {
        email: profile.email,
        phone: profile.phone ?? undefined,
        name: name || undefined,
        first_name: profile.first_name ?? undefined,
        middle_name: profile.patronymic ?? undefined,
        last_name: profile.last_name ?? undefined,
      })
    }
  }, [user, profile])

  // Microsoft Clarity user identification
  useEffect(() => {
    if (user && profile) {
      const fullName = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      window.clarity?.("identify", user.id, undefined, undefined, fullName)
      window.clarity?.("set", "email", profile.email)
      if (profile.phone) window.clarity?.("set", "phone", profile.phone)
      if (fullName) window.clarity?.("set", "name", fullName)
      window.clarity?.("set", "user_id", user.id)
    }
  }, [user, profile])

  // Tawk.to visitor identification
  useEffect(() => {
    if (user && profile) {
      if (tawkIdentifiedUserId.current === user.id) return

      const fullName = [profile.last_name, profile.first_name, profile.patronymic]
        .filter(Boolean)
        .join(' ')

      let tawkPhone: string | undefined
      if (profile.phone) {
        const cleaned = profile.phone.replace(/[\s\-()]/g, '')
        tawkPhone = cleaned.startsWith('+') ? cleaned
          : cleaned.startsWith('380') ? '+' + cleaned
          : cleaned.startsWith('0') ? '+380' + cleaned.slice(1)
          : '+' + cleaned
      }

      window.Tawk_API = window.Tawk_API || {}
      const visitor: { name: string; email: string; phone?: string } = { name: fullName, email: profile.email }
      if (tawkPhone) visitor.phone = tawkPhone
      window.Tawk_API.visitor = visitor

      const attrs: Record<string, string> = { name: fullName, email: profile.email }
      if (tawkPhone) attrs.phone = tawkPhone

      if (window.Tawk_API?.setAttributes) {
        window.Tawk_API.setAttributes(attrs, (error: unknown) => {
          if (error) logger.error('[Auth] Tawk.to setAttributes error:', error)
        })
      } else {
        const prev = window.Tawk_API?.onLoad
        window.Tawk_API!.onLoad = function () {
          prev?.()
          window.Tawk_API?.setAttributes?.(attrs, () => {})
        }
      }

      tawkIdentifiedUserId.current = user.id
    }
  }, [user, profile])

  // The scope is fixed by which wrapper below is called, never read from the
  // caller's arguments: signOut is handed straight to onClick in places, so a
  // positional scope parameter would silently receive a MouseEvent.
  const performSignOut = useCallback(async (scope: 'local' | 'global') => {
    try {
      await authService.signOut(scope)
    } catch (error) {
      logger.error('[Auth] Sign out error:', error)
      // The session cookies are HttpOnly, so only the server can delete them.
      // If the sign-out route itself failed, ask the clear route (no GoTrue
      // involved) – otherwise a reload would sign the user straight back in.
      await clearAuthSession()
    }
    // Always clear local state, whether or not the server call succeeded.
    markSignedOut()
    analytics.reset()
    tawkIdentifiedUserId.current = null
    setUser(null)
    setProfile(null)
    sessionCache.clear()
    invalidateOnLogout()
    queryClient.clear()
  }, [queryClient])

  /** Ends this device's session only. Other devices stay signed in. */
  const signOut = useCallback(() => performSignOut('local'), [performSignOut])

  /** Ends every session on every device, including this one. */
  const signOutAllDevices = useCallback(() => performSignOut('global'), [performSignOut])

  // CORE AUTH LOGIC – the server verified this request's session (middleware
  // → layout snapshot → initialUser/initialProfile) and owns the session from
  // here on: the refresh token is an HttpOnly cookie only the server refreshes.
  // The browser runs no auth engine; it follows the transitions the token
  // manager reports (src/lib/auth/accessToken.ts): 'signed-out' when the token
  // route says the session is gone (or another tab signed out), 'signed-in'
  // when another tab signed in.
  useEffect(() => {
    if (initialUser && initialProfile) {
      sessionCache.setProfile(initialProfile, initialUser.id)
    }
    if (initialUser) {
      checkAndLinkMigratedProfile(initialUser.id)
    }
    // Server snapshot had a user but the profile fetch timed out server-side.
    if (initialUser && !initialProfile) {
      refreshProfile(initialUser.id)
    }

    return onAuthTransition((transition) => {
      if (transition === 'signed-out') {
        logger.dev('[Auth] Signed out (token route or another tab)')
        analytics.reset()
        tawkIdentifiedUserId.current = null
        setUser(null)
        setProfile(null)
        sessionCache.clear()
        invalidateOnLogout()
        queryClient.clear()
      } else if (!userIdRef.current) {
        // Another tab signed in and this one is signed out: reload for a fresh
        // server snapshot. A signed-in tab is left alone (unsaved work).
        reloadPage()
      }
    })
  }, [refreshProfile, initialUser, initialProfile, queryClient])

  // Unknown at render (transient failure): ask the token route once. A real
  // signed-out answer arrives as nothing; a user arrives with their profile.
  useEffect(() => {
    if (!authUnknown || initialUser) return
    let cancelled = false
    void fetchTokenUser().then((tokenUser) => {
      if (cancelled || !tokenUser) return
      const recovered = tokenUser as unknown as User
      setUser(recovered)
      refreshProfile(recovered.id)
    })
    return () => {
      cancelled = true
    }
  }, [authUnknown, initialUser, refreshProfile])

  // Mobile tab resume: browsers suspend JS, so the token may be due. Asking
  // for it refreshes it on the server if needed; a dead session arrives as a
  // 'signed-out' transition above. A transient failure changes nothing.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !userIdRef.current) return
      void getAccessToken()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const value = useMemo(
    () => ({ user, profile, loading, profileLoading, signOut, signOutAllDevices, refreshProfile }),
    [user, profile, loading, profileLoading, signOut, signOutAllDevices, refreshProfile]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
